import { skyTerrainHeight } from '../../../packages/domain/src/87-sky-terrain.mjs';
import { SKY_WORLD } from '../../../packages/domain/src/86-sky-world-scale.mjs';

/** Short performance cues follow accepted damage and actual locomotion changes.
 * The simulation remains authoritative: no cue changes collision, aim or health.
 * A renderer owns one instance; simulation seconds freeze pause, rewind clears
 * history, and reduced motion consumes events without replaying them later. */
export function createSkyPilotCues() {
  let previous = null,
    active = null;
  function sample(state, { reduced = false } = {}) {
    const time = Number.isFinite(state.time) ? state.time : 0;
    const damage = state.damageTaken ?? 0;
    const groundAim =
      skyTerrainHeight(state.x, SKY_WORLD.pilotZ, state.terrainTravel ?? 0) +
      SKY_WORLD.pilotAimHeight;
    if (previous && (time < previous.time || damage < previous.damage)) {
      previous = null;
      active = null;
    }
    if (state.presentationPose || state.traversal || reduced || state.status === 'ready')
      active = null;
    else if (previous && state.status === 'playing') {
      if (damage > previous.damage) {
        const burst = state.bursts?.findLast((entry) => entry.effect === 'damage');
        const elapsed =
          burst && Number.isFinite(burst.duration) && Number.isFinite(burst.life)
            ? Math.max(0, burst.duration - burst.life)
            : 0;
        const stumble =
          state.lastDamage?.id === damage &&
          state.lastDamage?.source === 'collision' &&
          ['run', 'walk'].includes(state.locomotion) &&
          state.y <= groundAim + 0.15;
        active = {
          name: stumble ? 'Stumble' : 'HitBody',
          start: time - elapsed,
          duration: 0.55,
          scope: 'upper-body',
        };
      } else if (
        previous.locomotion === 'fly' &&
        ['run', 'walk'].includes(state.locomotion) &&
        state.y <= groundAim + 0.15
      ) {
        active = { name: 'Land', start: time, duration: 0.65, scope: 'transition' };
      }
    }
    previous = { time, damage, locomotion: state.locomotion };
    if (!active) return null;
    const age = Math.max(0, time - active.start);
    if (age >= active.duration) {
      active = null;
      return null;
    }
    const progress = age / active.duration;
    return {
      name: active.name,
      age,
      progress,
      duration: active.duration,
      weight: Math.sin(Math.PI * progress) ** 2,
      scope: active.scope,
    };
  }
  return { sample };
}

/** Full-body performances are reserved for input-free, grounded outcomes.
 * Recovery describes the escaped-guardian aftermath, never an inferred emotion.
 * Motion durations come from the actual exported clips; the camera may hold
 * longer, but cannot cut a supplied finite performance short. */
export function skyPilotEndingPerformance(kind, age, reduced, durations = {}) {
  const name =
    kind === 'pilot-down'
      ? 'Down'
      : ['departure', 'expedition-complete'].includes(kind)
        ? 'Cheer'
        : kind === 'guardian-escape'
          ? 'Recover'
          : null;
  if (!name) return null;
  const touchdown = 1;
  const clipDuration =
    Number.isFinite(durations[name]) && durations[name] > 0 ? durations[name] : 0;
  return {
    name,
    touchdown,
    minimumDuration: touchdown + clipDuration + 0.25,
    age: reduced ? clipDuration || 10 : Math.max(0, age - touchdown),
    active: reduced || age >= touchdown,
  };
}
